import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, ApiError } from '../api/client';
import './SerkenRenewalMigration.css';

type RunStatus =
  | 'queued'
  | 'running'
  | 'paused'
  | 'attention'
  | 'staged'
  | 'completed'
  | 'failed';
type ItemStatus = 'pending' | 'running' | 'done' | 'error';

/** Per-(phase, status) item count for a run, surfaced in the list's STAGE
 *  column. The list payload carries a `run_id` too; it is ignored here. */
interface StageCount {
  phase: string;
  status: ItemStatus;
  count: number;
}

/** A run is "settled" once it has stopped moving on its own - it may still be
 *  operator-actionable (paused/attention) or done (staged/completed/failed).
 *  In-flight is only queued/running. Used to gate detail polling and the
 *  retry/continue actions. */
const SETTLED_STATUSES: readonly RunStatus[] = [
  'paused',
  'attention',
  'staged',
  'completed',
  'failed',
];

function isSettled(status: RunStatus): boolean {
  return SETTLED_STATUSES.includes(status);
}

interface PanelOption {
  id: string;
  email: string;
}

interface RunInfo {
  id: string;
  status: RunStatus;
  stage_counts: StageCount[];
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  auto_retry_enabled: boolean;
  auto_retry_attempts_used: number;
  auto_retry_attempts_cap: number;
  triggered_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** Overflow auto-split (migration 053), read-only. 'overflow' runs are
   *  companions a primary run spawned to absorb domains that didn't fit its dest;
   *  parent_run_id links back to that primary. Optional so runs created before
   *  this feature (and any mock) still render. */
  kind?: 'primary' | 'overflow';
  parent_run_id?: string | null;
}

interface RunItem {
  source_email: string;
  dest_email: string;
  phase: string;
  status: ItemStatus;
  users_total: number | null;
  core_domain_users: number | null;
  dest_capacity: number | null;
  dest_free_seats: number | null;
  users_needed: number | null;
  oauth_client_ids: string[] | null;
  master_inbox_pairs: number | null;
  error_code: string | null;
  error_message: string | null;
  /** Allocate stamp (migration 053): the overflow decision settled. */
  allocated_at: string | null;
  /** Mirror resequence stamps (migration 056): claim-first order - every user
   *  created+licensed (dest_claimed_at), the source pool exhausted by mig-block
   *  users (source_blocked_at + count), then the fkgsheets resolve+push pass
   *  (passwords_resolved_at + count). Unresolved passwords never block. */
  dest_claimed_at: string | null;
  source_blocked_at: string | null;
  source_block_count: number | null;
  passwords_resolved_at: string | null;
  passwords_resolved_count: number | null;
  /** Replicate stamps. */
  oauth_trusted_at: string | null;
  ops_mapping_at: string | null;
  /** data_migrate connect/setup stamps - drive the DMS drill-down. */
  temp_admin_email: string | null;
  temp_admin_created_at: string | null;
  temp_admin_routed_at: string | null;
  dms_connection_requested_at: string | null;
  dms_authorized_at: string | null;
  dms_connected_at: string | null;
  /** Cutover tail + finalize stamps. */
  temp_admin_deleted_at: string | null;
  aliases_reapplied_at: string | null;
  resuspended_at: string | null;
  import_exited_at: string | null;
  finalize_verified_at: string | null;
  dms_started_at: string | null;
  dms_verified_at: string | null;
  preflight_completed_at: string | null;
  /** Overflow auto-split scope (migration 053), read-only. On a PARENT item,
   *  excluded_domains lists the customer domains peeled onto a companion overflow
   *  run. On an OVERFLOW item, included_domains scopes it to just the peeled
   *  domain(s). Optional so pre-feature items (and mocks) still render. */
  included_domains?: string[] | null;
  excluded_domains?: string[];
}

interface RunUser {
  source_email: string;
  user_email: string;
  full_name: string | null;
  suspended: boolean;
  aliases: string[];
  flags: string[];
  dest_holding_email: string | null;
  password: string | null;
  password_source: 'fkgsheets' | 'manual' | 'generated' | null;
  created_on_dest_at: string | null;
  license_assigned_at: string | null;
  photo_copied_at: string | null;
  imaps_pushed_at: string | null;
  dms_source_messages: number | null;
  dms_messages_copied: number | null;
  error_message: string | null;
}

/** One row of the cutover per-domain ledger (migration 050), one per (item,
 *  customer domain). Preflight writes the CF pin + flags; each cutover swap step
 *  stamps its own timestamp column. The stamps ARE the progress signal - a
 *  non-null timestamp means that swap step is done. */
interface RunDomain {
  run_id: string;
  source_email: string;
  domain: string;
  cf_account_id: number | null;
  cf_zone_id: string | null;
  flagged_reason: string | null;
  users_on_domain: number;
  source_users_renamed_at: string | null;
  source_aliases_stripped_at: string | null;
  removed_from_source_at: string | null;
  dest_inserted_at: string | null;
  verified_on_dest_at: string | null;
  dest_users_realized_at: string | null;
  dkim_at: string | null;
  error_code: string | null;
  error_message: string | null;
}

interface RunDetail {
  run: RunInfo;
  items: RunItem[];
  users: RunUser[];
  domains: RunDomain[];
}

/** One row of GET /api/migration-runs/cf-missing: a still-flagged (deferred)
 *  domain on an unfinished item, across ALL runs - the team's consolidated
 *  "move these into our CF" work list. */
interface CfMissingDomain {
  run_id: string;
  source_email: string;
  domain: string;
  flagged_reason: string;
  users_on_domain: number;
  run_created_at: string;
}

/** One message in the ops mailbox, addressed to a destination panel's admin@. */
interface OpsMessage {
  id: string;
  threadId: string;
  to: string | null;
  from: string | null;
  subject: string | null;
  date: string | null;
  snippet: string | null;
}

interface OpsMailResp {
  ops_mailbox: string;
  to_address: string;
  messages: OpsMessage[];
}

/** Per-pair ops-mail fetch state. Read-only and on demand - it is never polled,
 *  because each read costs a Gmail round-trip per message. */
interface OpsMailState {
  loading: boolean;
  error: string | null;
  data: OpsMailResp | null;
}

interface CreateRunResp {
  run_id: string;
  items_enqueued: { source_email: string; dest_email: string }[];
  skipped: { source_email: string; dest_email: string; reason: string }[];
}

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Playwright login failures embed the full challenge URL, which is a single
 *  unbroken token - it cannot wrap on whitespace, so the browser stacks it
 *  character-by-character and inflates the row past every other cell. Cap what
 *  we render; callers put the untruncated text in the cell's `title` so the
 *  operator never loses the failure detail (this is the only place it shows). */
const ERROR_DISPLAY_MAX = 140;

function truncateError(text: string): string {
  return text.length > ERROR_DISPLAY_MAX
    ? `${text.slice(0, ERROR_DISPLAY_MAX)}…`
    : text;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Mirror of the server-side parser, for live feedback only - the server
 *  remains the authority. */
function countPairs(text: string): { valid: number; invalid: number } {
  let valid = 0;
  let invalid = 0;
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line === '' || line.startsWith('#')) continue;
    const tokens = line.split(/[\s,;]+/).filter(Boolean);
    if (
      tokens.length === 2 &&
      EMAIL_RE.test(tokens[0]!.toLowerCase()) &&
      EMAIL_RE.test(tokens[1]!.toLowerCase()) &&
      tokens[0]!.toLowerCase() !== tokens[1]!.toLowerCase()
    ) {
      valid++;
    } else {
      invalid++;
    }
  }
  return { valid, invalid };
}

/** Short phase labels for the run-list STAGE chips and the per-item stepper. */
const PHASE_LABELS: Record<string, string> = {
  snapshot: 'snap',
  allocate: 'alloc',
  mirror: 'mirror',
  replicate: 'repl',
  data_migrate: 'dms',
  cutover: 'cutover',
  finalize: 'final',
};
const PHASE_SEQ = [
  'snapshot',
  'allocate',
  'mirror',
  'replicate',
  'data_migrate',
  'cutover',
  'finalize',
];

/** "2@mirror 1@dms 3@staged 1@err" - one chip per populated (phase|terminal)
 *  bucket, pipeline order. done@data_migrate reads as staged; errors pool. */
function stageSummary(counts: StageCount[]): { label: string; kind: 'active' | 'staged' | 'error' }[] {
  const chips: { label: string; kind: 'active' | 'staged' | 'error' }[] = [];
  const errors = counts.filter((c) => c.status === 'error').reduce((s, c) => s + c.count, 0);
  const staged = counts
    .filter((c) => c.status === 'done' && c.phase === 'data_migrate')
    .reduce((s, c) => s + c.count, 0);
  for (const phase of PHASE_SEQ) {
    const active = counts
      .filter((c) => c.phase === phase && (c.status === 'pending' || c.status === 'running'))
      .reduce((s, c) => s + c.count, 0);
    if (active > 0) chips.push({ label: `${active}@${PHASE_LABELS[phase] ?? phase}`, kind: 'active' });
    const parkedDone = counts
      .filter((c) => c.phase === phase && c.status === 'done' && phase !== 'data_migrate')
      .reduce((s, c) => s + c.count, 0);
    if (parkedDone > 0) chips.push({ label: `${parkedDone}@${PHASE_LABELS[phase] ?? phase}✓`, kind: 'active' });
  }
  if (staged > 0) chips.push({ label: `${staged} staged`, kind: 'staged' });
  if (errors > 0) chips.push({ label: `${errors} err`, kind: 'error' });
  return chips;
}

/** DMS copy progress for one pair: destination messages / source messages, over
 *  every user of that source with a known source count. Null until preflight
 *  stamps the source totals. */
function phasePct(users: RunUser[], sourceEmail: string): number | null {
  const mine = users.filter((u) => u.source_email === sourceEmail && u.dms_source_messages !== null);
  if (mine.length === 0) return null;
  const src = mine.reduce((s, u) => s + (u.dms_source_messages ?? 0), 0);
  const cop = mine.reduce((s, u) => s + (u.dms_messages_copied ?? 0), 0);
  return src === 0 ? 100 : Math.min(100, Math.floor((cop / src) * 100));
}

/** Stop condition for the bounded post-Preflight refresh loop: the worker has
 *  written the ledger once every staged item (phase data_migrate/cutover) carries
 *  a preflight stamp. The stamp now lands regardless of flags (a flagged domain
 *  is DEFERRED at cutover, not a blocker), so the stamp alone is the signal.
 *  Evaluated against the freshly fetched detail each tick. */
function preflightLedgerReady(d: RunDetail): boolean {
  const staged = d.items.filter((i) => i.phase === 'data_migrate' || i.phase === 'cutover');
  return staged.length > 0 && staged.every((i) => i.preflight_completed_at !== null);
}

/** The five phases an item walks before cutover. A phase past this window
 *  (cutover, finalize) or an unknown phase reads as all-five-done via the
 *  `idx === -1` fall-through in StepperCell. */
const STEPPER = ['snapshot', 'allocate', 'mirror', 'replicate', 'data_migrate'] as const;

function StepperCell({ item, users }: { item: RunItem; users: RunUser[] }): JSX.Element {
  const idx = STEPPER.indexOf(item.phase as (typeof STEPPER)[number]);
  const pct =
    item.phase === 'data_migrate' && item.status === 'running'
      ? phasePct(users, item.source_email)
      : null;
  return (
    <td className="srm-steppercell">
      {STEPPER.map((p, i) => {
        const state =
          idx === -1 || i < idx
            ? 'done'
            : i === idx
              ? item.status === 'done'
                ? 'done'
                : item.status === 'error'
                  ? 'error'
                  : 'current'
              : 'todo';
        return (
          <span key={p} className={`srm-step srm-step--${state}`} title={p}>
            {PHASE_LABELS[p]}
          </span>
        );
      })}
      {pct !== null && <span className="srm-step-pct">{pct}%</span>}
    </td>
  );
}

/** The seven cutover swap steps, in the order the processor stamps them (mirror
 *  of SWAP_ORDER in src/lib/migration-cutover.ts). Each maps to a ledger
 *  timestamp column; a non-null value means that step is done for the domain. */
type DomainStepKey =
  | 'source_users_renamed_at'
  | 'source_aliases_stripped_at'
  | 'removed_from_source_at'
  | 'dest_inserted_at'
  | 'verified_on_dest_at'
  | 'dest_users_realized_at'
  | 'dkim_at';

const DOMAIN_STEPS: { label: string; col: DomainStepKey }[] = [
  { label: 'renamed', col: 'source_users_renamed_at' },
  { label: 'stripped', col: 'source_aliases_stripped_at' },
  { label: 'removed', col: 'removed_from_source_at' },
  { label: 'inserted', col: 'dest_inserted_at' },
  { label: 'verified', col: 'verified_on_dest_at' },
  { label: 'realized', col: 'dest_users_realized_at' },
  { label: 'dkim', col: 'dkim_at' },
];

/** Per-item customer-domain ledger, shown inside an expanded pair. Preflight
 *  populates it; the seven mini-chips reuse the phase stepper's visual language
 *  (done vs todo) to show how far each domain has walked the swap. A flagged
 *  domain or a per-row error renders in the same rose error style as item
 *  errors. Empty until preflight has mapped the panel's domains. */
function DomainTable({ domains }: { domains: RunDomain[] }): JSX.Element {
  if (domains.length === 0) {
    return (
      <div className="srm-domain-empty">
        No customer domains mapped yet - run Preflight to populate the cutover ledger.
      </div>
    );
  }
  return (
    <table className="srm-domaintable">
      <thead>
        <tr>
          <th>Domain</th>
          <th>CF account</th>
          <th>Swap progress</th>
          <th>Flag</th>
        </tr>
      </thead>
      <tbody>
        {domains.map((d) => {
          const rowError = d.error_message
            ? `${d.error_code ?? ''} ${d.error_message}`.trim()
            : '';
          return (
            <tr key={d.domain}>
              <td className="srm-cell-mono">
                {d.domain}
                <div className="srm-cell-faint">
                  {d.users_on_domain} user{d.users_on_domain === 1 ? '' : 's'}
                </div>
              </td>
              <td className="srm-cell-mono">{d.cf_account_id ?? '-'}</td>
              <td className="srm-steppercell">
                {DOMAIN_STEPS.map((s) => {
                  const done = d[s.col] !== null;
                  return (
                    <span
                      key={s.label}
                      className={`srm-step srm-step--${done ? 'done' : 'todo'}`}
                      title={s.label}
                    >
                      {s.label}
                    </span>
                  );
                })}
              </td>
              <td>
                {d.flagged_reason && (
                  <span className="srm-cell-error" title={d.flagged_reason}>
                    {d.flagged_reason}
                  </span>
                )}
                {rowError && (
                  <span className="srm-cell-error" title={rowError}>
                    {truncateError(rowError)}
                  </span>
                )}
                {!d.flagged_reason && !rowError && '-'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Pipeline rail + per-stage drill-down ────────────────────────────────────
//
// The run detail's primary navigation: the WHOLE pipeline laid out in execution
// order - automated phases, the two hard stops, and the human actions at each -
// so the operator always sees where the run is, what happens next, and where
// they will have to act. Clicking a phase opens its drill-down: per-panel
// sub-step breakdown (mirror: claim/block/passwords; replicate: photos/OAuth/
// routing; data migrate: connect/copy/verify; ...), searchable by panel, user
// or domain.

const phaseIdx = (p: string): number => PHASE_SEQ.indexOf(p);

/** Where one item stands relative to a pipeline phase. */
function itemStageState(item: RunItem, stage: string): 'done' | 'active' | 'error' | 'todo' {
  const ip = phaseIdx(item.phase);
  const sp = phaseIdx(stage);
  if (ip > sp) return 'done';
  if (ip < sp) return 'todo';
  if (item.status === 'done') return 'done';
  if (item.status === 'error') return 'error';
  return 'active';
}

interface RailStage {
  key: string;
  label: string;
  desc: string;
  kind: 'human' | 'auto' | 'stop';
}

/** The pipeline in exact execution order (mirrors the processor's PHASE_ORDER +
 *  the two parks nextSafePhase enforces). Mirror's desc carries the 2026-07-24
 *  resequence: claim first, block the source pool, passwords LAST (and never a
 *  blocker). */
const RAIL: RailStage[] = [
  { key: 'create', label: 'Create run', desc: 'You: paste source → dest pairs', kind: 'human' },
  { key: 'snapshot', label: '1 · Snapshot', desc: 'Read every source user (read-only)', kind: 'auto' },
  { key: 'allocate', label: '2 · Allocate', desc: 'Overflow → one dedicated spare per domain', kind: 'auto' },
  { key: 'mirror', label: '3 · Mirror', desc: 'Claim seats → block source pool → passwords last', kind: 'auto' },
  { key: 'replicate', label: '4 · Replicate', desc: 'Photos · OAuth trust · routing map', kind: 'auto' },
  { key: 'data_migrate', label: '5 · Data migrate', desc: 'Copy every mailbox (hours, polled 15 min)', kind: 'auto' },
  { key: 'stop:staged', label: 'HARD STOP · staged', desc: 'You: Preflight, then type CUTOVER', kind: 'stop' },
  { key: 'cutover', label: '6 · Cutover', desc: 'THE SWAP · 7 steps per domain', kind: 'auto' },
  { key: 'stop:cutover', label: 'HARD STOP', desc: 'You: type FINALIZE', kind: 'stop' },
  { key: 'finalize', label: '7 · Finalize', desc: 'Exit import · verify end state', kind: 'auto' },
];

function PipelineRail({
  items,
  activeStage,
  onSelect,
}: {
  items: RunItem[];
  activeStage: string | null;
  onSelect: (stage: string | null) => void;
}): JSX.Element {
  return (
    <div className="srm-rail">
      {RAIL.map((s, i) => {
        const isPhase = phaseIdx(s.key) !== -1;
        let cls = 'todo';
        let counts: JSX.Element | null = null;
        if (s.key === 'create') {
          cls = 'done';
        } else if (isPhase) {
          const states = items.map((it) => itemStageState(it, s.key));
          const done = states.filter((x) => x === 'done').length;
          const active = states.filter((x) => x === 'active').length;
          const errored = states.filter((x) => x === 'error').length;
          cls =
            errored > 0 ? 'error' : active > 0 ? 'active' : done === items.length && items.length > 0 ? 'done' : 'todo';
          counts = (
            <span className="srm-rail-counts">
              {done > 0 && <span className="srm-rail-count srm-rail-count--done">{done}✓</span>}
              {active > 0 && <span className="srm-rail-count srm-rail-count--active">{active}▶</span>}
              {errored > 0 && <span className="srm-rail-count srm-rail-count--err">{errored}✕</span>}
            </span>
          );
        } else {
          // Hard stops: reached once every item is done AT the phase before it;
          // passed once any item moved beyond it.
          const before = s.key === 'stop:staged' ? 'data_migrate' : 'cutover';
          const after = s.key === 'stop:staged' ? 'cutover' : 'finalize';
          const passed = items.some((it) => phaseIdx(it.phase) >= phaseIdx(after));
          const reached =
            items.length > 0 && items.every((it) => itemStageState(it, before) === 'done');
          cls = passed ? 'done' : reached ? 'reached' : 'todo';
        }
        return (
          <div key={s.key} className="srm-rail-seg">
            {i > 0 && <span className="srm-rail-arrow">→</span>}
            <button
              type="button"
              className={`srm-rail-node srm-rail-node--${s.kind} srm-rail-node--${cls} ${
                activeStage === s.key ? 'srm-rail-node--selected' : ''
              }`}
              onClick={() => isPhase && onSelect(activeStage === s.key ? null : s.key)}
              disabled={!isPhase}
              title={isPhase ? 'Click for the per-panel breakdown' : s.desc}
            >
              <span className="srm-rail-label">
                {s.kind === 'stop' && '⏸ '}
                {s.kind === 'human' && '👤 '}
                {s.label}
              </span>
              <span className="srm-rail-desc">{s.desc}</span>
              {counts}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** One sub-step chip inside a drill row. Reuses the stepper's visual states. */
function Sub({
  state,
  label,
  title,
}: {
  state: 'done' | 'current' | 'todo' | 'error';
  label: string;
  title?: string;
}): JSX.Element {
  return (
    <span className={`srm-step srm-step--${state}`} title={title}>
      {label}
    </span>
  );
}

const doneOr = (stamp: string | null, active: boolean): 'done' | 'current' | 'todo' =>
  stamp ? 'done' : active ? 'current' : 'todo';

/** Case-insensitive match of one pair against the drill search: panel emails,
 *  user emails, holding addresses, aliases and customer domains all count. */
function pairMatches(
  q: string,
  item: RunItem,
  users: RunUser[],
  domains: RunDomain[],
): boolean {
  if (q === '') return true;
  if (item.source_email.includes(q) || item.dest_email.includes(q)) return true;
  if (
    users.some(
      (u) =>
        u.user_email.includes(q) ||
        (u.dest_holding_email ?? '').includes(q) ||
        u.aliases.some((a) => a.toLowerCase().includes(q)),
    )
  ) {
    return true;
  }
  return domains.some((d) => d.domain.includes(q));
}

/** Per-panel drill table for one pipeline stage. Search (already lowercased)
 *  filters panels; inside mirror/data-migrate it ALSO surfaces the matching
 *  user rows, so "find this mailbox" is one keystroke away. */
function StageDrill({
  stage,
  items,
  usersBySource,
  domainsBySource,
  search,
  expanded,
  onToggle,
}: {
  stage: string;
  items: RunItem[];
  usersBySource: Map<string, RunUser[]>;
  domainsBySource: Map<string, RunDomain[]>;
  search: string;
  expanded: string | null;
  onToggle: (source: string) => void;
}): JSX.Element {
  const q = search.trim().toLowerCase();
  const visible = items.filter((it) =>
    pairMatches(q, it, usersBySource.get(it.source_email) ?? [], domainsBySource.get(it.source_email) ?? []),
  );
  if (visible.length === 0) {
    return <div className="srm-domain-empty">No panels match "{search}".</div>;
  }

  /** The user rows to show under a panel: all when expanded, else the search hits. */
  const userRows = (it: RunItem): RunUser[] => {
    const users = usersBySource.get(it.source_email) ?? [];
    if (expanded === it.source_email) return users;
    if (q === '') return [];
    return users.filter(
      (u) =>
        u.user_email.includes(q) ||
        (u.dest_holding_email ?? '').includes(q) ||
        u.aliases.some((a) => a.toLowerCase().includes(q)),
    );
  };

  return (
    <div className="srm-table-wrap">
      <table className="srm-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Destination</th>
            <th>{stage === 'cutover' ? 'Tail' : 'Sub-steps'}</th>
            <th className="srm-col-center">Status</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((it) => {
            const users = usersBySource.get(it.source_email) ?? [];
            const domains = domainsBySource.get(it.source_email) ?? [];
            const state = itemStageState(it, stage);
            const active = state === 'active' || state === 'error';
            const created = users.filter((u) => u.created_on_dest_at).length;
            const licensed = users.filter((u) => u.license_assigned_at).length;
            const photos = users.filter((u) => u.photo_copied_at).length;
            const real = users.filter(
              (u) => u.password_source === 'fkgsheets' || u.password_source === 'manual',
            ).length;
            const imaps = users.filter((u) => u.imaps_pushed_at).length;
            const fullError = it.error_message
              ? `${it.error_code ?? ''} ${it.error_message}`.trim()
              : '';
            const subs = ((): JSX.Element => {
              switch (stage) {
                case 'snapshot':
                  return (
                    <>
                      <Sub state={it.users_total !== null ? 'done' : active ? 'current' : 'todo'} label={`${it.users_total ?? '…'} users`} title="Users read off the source" />
                      {(it.core_domain_users ?? 0) > 0 && (
                        <Sub state="done" label={`${it.core_domain_users} core`} title="On the source CORE domain - these addresses can never move" />
                      )}
                    </>
                  );
                case 'allocate':
                  return (
                    <>
                      <Sub state={doneOr(it.allocated_at, active)} label="allocated" title="Overflow decision settled" />
                      {(it.excluded_domains ?? []).map((d) => (
                        <Sub key={d} state="done" label={`peeled ${d}`} title="Moved to its own dedicated spare panel (companion overflow run)" />
                      ))}
                      {(it.included_domains ?? [])?.map((d) => (
                        <Sub key={d} state="done" label={`scope ${d}`} title="This overflow item is scoped to just this domain" />
                      ))}
                    </>
                  );
                case 'mirror':
                  return (
                    <>
                      <Sub
                        state={it.dest_claimed_at ? 'done' : active ? 'current' : 'todo'}
                        label={`claim ${created}/${users.length}`}
                        title="Create + license every user on the destination with a RANDOM password - the seat/license claim, first and fastest"
                      />
                      <Sub
                        state={doneOr(it.source_blocked_at, active && it.dest_claimed_at !== null)}
                        label={`block src${it.source_block_count !== null ? ` (${it.source_block_count})` : ''}`}
                        title="Exhaust the SOURCE's license pool with mig-block users so nothing new can be built on it"
                      />
                      <Sub
                        state={doneOr(it.passwords_resolved_at, active && it.source_blocked_at !== null)}
                        label={`passwords ${real}/${users.length}`}
                        title="fkgsheets resolve, pushed onto the created users. Runs LAST - an unresolved password NEVER blocks the run (generated stands until backfill)"
                      />
                      <Sub state={licensed === users.length && users.length > 0 ? 'done' : 'todo'} label={`licensed ${licensed}/${users.length}`} title="License assignments on the destination" />
                    </>
                  );
                case 'replicate':
                  return (
                    <>
                      <Sub state={photos === users.length && users.length > 0 ? 'done' : active ? 'current' : 'todo'} label={`photos ${photos}/${users.length}`} title="Profile photos copied source → dest" />
                      <Sub state={doneOr(it.oauth_trusted_at, active)} label={`oauth ${(it.oauth_client_ids ?? []).length}`} title="Source's trusted OAuth apps re-trusted on the destination" />
                      <Sub state={doneOr(it.ops_mapping_at, active)} label={`routing ${it.master_inbox_pairs ?? 0}`} title="Consolidated routing map incl. the MANDATORY admin@dest → ops mailbox entry" />
                    </>
                  );
                case 'data_migrate': {
                  const pct = phasePct(users, it.source_email);
                  return (
                    <>
                      <Sub state={doneOr(it.temp_admin_created_at, active)} label="temp admin" title={`Temp super admin on the source${it.temp_admin_email ? `: ${it.temp_admin_email}` : ''}`} />
                      <Sub state={doneOr(it.temp_admin_routed_at, active)} label="routed" title="Temp admin's mail routed to the ops mailbox" />
                      <Sub state={doneOr(it.dms_authorized_at, active)} label="authorized" title="Consent link clicked AS the temp admin" />
                      <Sub state={doneOr(it.dms_connected_at, active)} label="connected" title="Dest wizard connected to the source" />
                      <Sub state={doneOr(it.dms_started_at, active)} label="import" title="Google's data import running" />
                      <Sub state={it.dms_verified_at ? 'done' : it.dms_started_at ? 'current' : 'todo'} label={`copy ${pct !== null ? `${pct}%` : '…'}`} title="Destination message counts vs source totals" />
                      <Sub state={doneOr(it.dms_verified_at, false)} label="verified" title="Console report Complete + counts within tolerance" />
                    </>
                  );
                }
                case 'cutover':
                  return (
                    <>
                      <Sub state={doneOr(it.temp_admin_deleted_at, active)} label="temp admin gone" title="Temp source admin deleted" />
                      <Sub state={doneOr(it.aliases_reapplied_at, active)} label="aliases" title="Aliases re-applied on swapped domains (final pass)" />
                      <Sub state={doneOr(it.resuspended_at, active)} label="re-suspend" title="Source-suspended users re-suspended on the dest (final pass)" />
                      <Sub state={imaps === users.length && users.length > 0 ? 'done' : imaps > 0 ? 'current' : 'todo'} label={`imaps ${imaps}/${users.length}`} title="Realized users pushed to the IMAPs orchestrator - always the LAST per-panel step" />
                    </>
                  );
                case 'finalize':
                  return (
                    <>
                      <Sub state={doneOr(it.import_exited_at, active)} label="import exited" title="Parked data import torn down on the destination" />
                      <Sub state={doneOr(it.finalize_verified_at, active)} label="verified" title="Every domain on dest, none on source, suspended users suspended" />
                    </>
                  );
                default:
                  return <></>;
              }
            })();
            const showDomains = stage === 'cutover' && (expanded === it.source_email || q !== '');
            const drillDomains =
              q === '' || expanded === it.source_email
                ? domains
                : domains.filter((d) => d.domain.includes(q));
            return [
              <tr
                key={it.source_email}
                className="srm-row--clickable"
                onClick={() => onToggle(it.source_email)}
              >
                <td className="srm-cell-mono">{it.source_email}</td>
                <td className="srm-cell-mono">{it.dest_email}</td>
                <td className="srm-steppercell">{subs}</td>
                <td className="srm-col-center">
                  <span className={`srm-status srm-status--${state === 'active' ? it.status : state === 'todo' ? 'pending' : state}`}>
                    {state === 'todo' ? 'waiting' : state === 'active' ? it.status : state}
                  </span>
                </td>
                <td className="srm-cell-error" title={fullError || undefined}>
                  {fullError ? truncateError(fullError) : '-'}
                </td>
              </tr>,
              ...(showDomains && drillDomains.length > 0
                ? [
                    <tr key={`${it.source_email}:dom`} className="srm-userrow">
                      <td colSpan={5} className="srm-domaincell">
                        <DomainTable domains={drillDomains} />
                      </td>
                    </tr>,
                  ]
                : []),
              ...userRows(it).map((u) => (
                <tr key={`${it.source_email}:${u.user_email}`} className="srm-userrow">
                  <td className="srm-cell-mono">
                    {u.user_email}
                    {u.full_name && <div className="srm-cell-faint">{u.full_name}</div>}
                  </td>
                  <td className="srm-cell-mono">{u.dest_holding_email ?? '-'}</td>
                  <td className="srm-cell-mono">
                    {u.password ?? '-'}
                    {u.password_source && (
                      <>
                        {' '}
                        <span
                          className={`srm-flag ${u.password_source === 'generated' ? 'srm-flag--warn' : ''}`}
                          title={
                            u.password_source === 'generated'
                              ? 'Random claim-time password - replaced when fkgsheets/manual resolves'
                              : u.password_source === 'manual'
                                ? 'Entered by an operator (never overwritten)'
                                : 'Resolved from fkgsheets and pushed to Google'
                          }
                        >
                          {u.password_source}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="srm-col-center srm-cell-faint">
                    {u.created_on_dest_at ? 'created' : '-'}
                    {u.license_assigned_at ? ' · licensed' : ''}
                    {u.photo_copied_at ? ' · photo' : ''}
                    {u.imaps_pushed_at ? ' · imaps' : ''}
                    {u.dms_messages_copied !== null ? ` · ${u.dms_messages_copied}/${u.dms_source_messages ?? '?'} mail` : ''}
                  </td>
                  <td className="srm-cell-error" title={u.error_message ?? undefined}>
                    {u.error_message ? truncateError(u.error_message) : ''}
                  </td>
                </tr>
              )),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Read-only list of the ops-mailbox messages for one pair's destination admin@.
 *  Deliberately plain: this exists so the operator can watch the data-migration
 *  mail arrive, not to act on it. */
function OpsMailPanel({
  state,
  destEmail,
  onRefresh,
}: {
  state: OpsMailState | undefined;
  destEmail: string;
  onRefresh: () => void;
}): JSX.Element {
  const messages = state?.data?.messages ?? [];
  return (
    <div>
      <div className="srm-opsmail-head">
        <span>
          Mail to <code className="srm-cell-mono">{destEmail}</code>
          {state?.data && (
            <>
              {' '}
              in <code className="srm-cell-mono">{state.data.ops_mailbox}</code>
            </>
          )}
        </span>
        <button
          type="button"
          className="srm-btn srm-btn--ghost srm-btn--small"
          onClick={onRefresh}
          disabled={state?.loading === true}
        >
          {state?.loading === true ? 'Reading…' : 'Refresh'}
        </button>
      </div>

      {state?.error != null && <div className="srm-cell-error">{state.error}</div>}

      {state?.error == null && !state?.loading && messages.length === 0 && (
        <div className="srm-opsmail-empty">No mail yet for this destination panel.</div>
      )}

      {messages.length > 0 && (
        <div className="srm-opsmail-list">
          <div className="srm-opsmail-msg srm-opsmail-msg--head">
            <span>To</span>
            <span>From</span>
            <span>Subject</span>
            <span>Date</span>
          </div>
          {messages.map((m) => (
            <div key={m.id} className="srm-opsmail-msg">
              <span className="srm-cell-mono">{m.to ?? '-'}</span>
              <span className="srm-cell-mono">{m.from ?? '-'}</span>
              <span title={m.snippet ?? undefined}>
                {m.subject ?? '(no subject)'}
                {m.snippet && <div className="srm-cell-faint">{m.snippet}</div>}
              </span>
              <span className="srm-cell-faint">{m.date ?? '-'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Read-only badge on an overflow run: it names the primary run that spawned it
 *  (first 8 chars of parent_run_id) and opens that parent on click. Reuses the
 *  component's established ghost-button pattern - the same small clickable used
 *  for Back / Ops mail - so no new visual style is introduced. `onOpen` receives
 *  the full parent id; the caller (list row) stops row-click propagation. */
function OverflowParentLink({
  parentRunId,
  onOpen,
}: {
  parentRunId: string | null | undefined;
  onOpen: (id: string) => void;
}): JSX.Element {
  const short = parentRunId ? parentRunId.slice(0, 8) : 'unknown';
  return (
    <button
      type="button"
      className="srm-btn srm-btn--ghost srm-btn--small"
      disabled={!parentRunId}
      onClick={(e) => {
        e.stopPropagation();
        if (parentRunId) onOpen(parentRunId);
      }}
      title={parentRunId ? `Open parent run ${parentRunId}` : 'Parent run unknown'}
    >
      ↑ Overflow of {short}
    </button>
  );
}

export function SerkenRenewalMigration(): JSX.Element {
  const [panels, setPanels] = useState<PanelOption[]>([]);
  const [search, setSearch] = useState('');
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const [destBySource, setDestBySource] = useState<Record<string, string>>({});
  const [bulkText, setBulkText] = useState('');
  const [starting, setStarting] = useState(false);
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [cfMissing, setCfMissing] = useState<CfMissingDomain[]>([]);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  // Pipeline drill-down: which stage's per-panel breakdown is open (null =
  // the overview pairs table), its search box, and its expanded panel.
  const [stageDrill, setStageDrill] = useState<string | null>(null);
  const [drillSearch, setDrillSearch] = useState('');
  const [drillExpanded, setDrillExpanded] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [phaseBusy, setPhaseBusy] = useState(false);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightRefreshing, setPreflightRefreshing] = useState(false);
  const [cutoverBusy, setCutoverBusy] = useState(false);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [cutoverConfirmOpen, setCutoverConfirmOpen] = useState(false);
  const [cutoverTyped, setCutoverTyped] = useState('');
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
  const [finalizeTyped, setFinalizeTyped] = useState('');
  const [pwDraft, setPwDraft] = useState<Record<string, string>>({});
  const [opsOpen, setOpsOpen] = useState<string | null>(null);
  const [opsMail, setOpsMail] = useState<Record<string, OpsMailState>>({});
  const [toast, setToast] = useState<Toast | null>(null);
  const detailPollRef = useRef<number | null>(null);
  const preflightLoopRef = useRef<number | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => {
    api<PanelOption[]>('/api/panels')
      .then((p) => setPanels(p))
      .catch(() => showToast('error', 'Failed to load panels list'));
  }, [showToast]);

  const filteredPanels = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q === '' ? panels : panels.filter((p) => p.email.toLowerCase().includes(q));
    return base.slice(0, 200);
  }, [panels, search]);

  /** Pairs from the picker: only rows whose dest is a valid, different email. */
  const pickerPairs = useMemo(() => {
    const out: { source: string; dest: string }[] = [];
    for (const src of selectedSources) {
      const dest = (destBySource[src] ?? '').trim().toLowerCase();
      if (EMAIL_RE.test(dest) && dest !== src) out.push({ source: src, dest });
    }
    return out;
  }, [selectedSources, destBySource]);

  const bulkCount = useMemo(() => countPairs(bulkText), [bulkText]);
  const totalPairs = pickerPairs.length + bulkCount.valid;

  /** The server accepts one pairs_text - serialize picker pairs + bulk lines. */
  function assemblePairsText(): string {
    const pickerLines = pickerPairs.map((p) => `${p.source},${p.dest}`);
    return [...pickerLines, bulkText].join('\n');
  }

  const refreshRuns = useCallback(async (): Promise<void> => {
    try {
      const r = await api<{ runs: RunInfo[] }>('/api/migration-runs?limit=15');
      setRuns(r.runs);
    } catch {
      // transient - next poll retries
    }
    // The consolidated CF-missing (deferred) domain list rides the same cadence:
    // it shrinks as the team fixes CF + re-preflights, so it must stay fresh.
    try {
      const c = await api<{ domains: CfMissingDomain[] }>('/api/migration-runs/cf-missing');
      setCfMissing(c.domains);
    } catch {
      // transient - next poll retries
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  // List poll: 3s while a run is in flight, else 7s.
  useEffect(() => {
    const hasInflight = runs.some((r) => r.status === 'queued' || r.status === 'running');
    const timer = window.setInterval(() => void refreshRuns(), hasInflight ? 3000 : 7000);
    return () => window.clearInterval(timer);
  }, [runs, refreshRuns]);

  const refreshDetail = useCallback(
    async (runId: string): Promise<RunDetail | null> => {
      try {
        const d = await api<RunDetail>(`/api/migration-runs/${runId}`);
        setDetail(d);
        return d;
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Failed to load run');
        return null;
      }
    },
    [showToast],
  );

  // Bounded post-Preflight refresh loop. Preflight returns immediately but the
  // worker writes the domain ledger seconds-to-minutes later WITHOUT moving run
  // status, so the settled-run detail poll never wakes and the domain table /
  // summary / flag banner stay empty until reopen. This nudges the detail every
  // 2.5s for up to 60s (own ref, so it never collides with detailPollRef), stopping
  // early once the ledger lands. Mirrors the detail poll's ref-based cleanup.
  const stopPreflightLoop = useCallback((): void => {
    if (preflightLoopRef.current !== null) {
      window.clearInterval(preflightLoopRef.current);
      preflightLoopRef.current = null;
    }
    setPreflightRefreshing(false);
  }, []);

  const startPreflightLoop = useCallback(
    (runId: string): void => {
      stopPreflightLoop(); // a second Preflight click restarts the window
      setPreflightRefreshing(true);
      const deadline = Date.now() + 60_000;
      preflightLoopRef.current = window.setInterval(() => {
        void (async () => {
          const d = await refreshDetail(runId);
          if ((d !== null && preflightLedgerReady(d)) || Date.now() >= deadline) {
            stopPreflightLoop();
          }
        })();
      }, 2500);
    },
    [refreshDetail, stopPreflightLoop],
  );

  // Detail poll: 2.5s until terminal.
  useEffect(() => {
    if (detailPollRef.current !== null) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
    if (!openRunId) {
      setDetail(null);
      setExpandedItem(null);
      setStageDrill(null);
      setDrillSearch('');
      setDrillExpanded(null);
      setOpsOpen(null);
      setOpsMail({});
      setCutoverConfirmOpen(false);
      setCutoverTyped('');
      setFinalizeConfirmOpen(false);
      setFinalizeTyped('');
      return;
    }
    void refreshDetail(openRunId);
    detailPollRef.current = window.setInterval(() => void refreshDetail(openRunId), 2500);
    return () => {
      if (detailPollRef.current !== null) window.clearInterval(detailPollRef.current);
    };
  }, [openRunId, refreshDetail]);

  // Detail poll gate: stop the 2.5s poll once the run settles (paused/attention/
  // staged/completed/failed - a settled run does not move on its own, so polling
  // it burns requests). Restart it whenever the run is back in flight - after a
  // "Continue run" the backend re-opens a paused run to running, and that
  // refreshDetail lands here without re-running the open effect above.
  useEffect(() => {
    if (!detail || !openRunId) return;
    const settled = isSettled(detail.run.status);
    if (settled && detailPollRef.current !== null) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    } else if (!settled && detailPollRef.current === null) {
      detailPollRef.current = window.setInterval(() => void refreshDetail(openRunId), 2500);
    }
  }, [detail, openRunId, refreshDetail]);

  // Kill the bounded preflight loop when the run detail closes (openRunId changes)
  // or the component unmounts - same ref-clearing cleanup the detail poll uses.
  useEffect(() => stopPreflightLoop, [openRunId, stopPreflightLoop]);

  function toggleSource(email: string): void {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  async function startRun(): Promise<void> {
    setStarting(true);
    try {
      const r = await api<CreateRunResp>('/api/migration-runs', {
        method: 'POST',
        body: JSON.stringify({ pairs_text: assemblePairsText() }),
      });
      const notes: string[] = [];
      if (r.skipped.length > 0) {
        const byReason = new Map<string, number>();
        for (const s of r.skipped) byReason.set(s.reason, (byReason.get(s.reason) ?? 0) + 1);
        notes.push(
          `skipped ${r.skipped.length} (${[...byReason].map(([k, v]) => `${k}: ${v}`).join(', ')})`,
        );
      }
      if (r.run_id) {
        showToast(
          'success',
          `Run started - ${r.items_enqueued.length} pair(s)${notes.length ? ` - ${notes.join(' - ')}` : ''}`,
        );
        setBulkText('');
        setSelectedSources(new Set());
        setDestBySource({});
        await refreshRuns();
        setOpenRunId(r.run_id);
      } else {
        showToast('error', `Nothing to enqueue - ${notes.join(' - ') || 'no pairs matched'}`);
      }
    } catch (err) {
      const e = err as ApiError;
      const lineErrors = (e.data as { errors?: { line: number; reason: string }[] } | undefined)
        ?.errors;
      showToast(
        'error',
        `${e.message}${
          lineErrors?.length
            ? ` - ${lineErrors
                .slice(0, 3)
                .map((le) => `line ${le.line}: ${le.reason}`)
                .join('; ')}${lineErrors.length > 3 ? '…' : ''}`
            : ''
        }`,
      );
    } finally {
      setStarting(false);
    }
  }

  async function retryErrors(runId: string): Promise<void> {
    setRetrying(true);
    try {
      const r = await api<{ retried: number }>(`/api/migration-runs/${runId}/retry-errors`, {
        method: 'POST',
      });
      showToast(
        r.retried > 0 ? 'success' : 'info',
        r.retried > 0 ? `Re-enqueued ${r.retried} item(s)` : 'No errored items to retry',
      );
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  /** Advance every parked (phase-done) panel to its next safe phase and enqueue
   *  it. Auto-advance handles healthy runs; this is the operator's manual heal
   *  for a run that settled to `paused` (e.g. a panel that finished a phase
   *  before auto-advance, or a Redis blip). The server only advances items that
   *  are `done` at snapshot/mirror/replicate. */
  async function continueRun(runId: string): Promise<void> {
    setPhaseBusy(true);
    try {
      const r = await api<{ started: number }>(`/api/migration-runs/${runId}/continue`, {
        method: 'POST',
      });
      showToast(
        r.started > 0 ? 'success' : 'info',
        r.started > 0
          ? `${r.started} panel(s) continued`
          : 'Nothing to continue - all panels are moving or staged',
      );
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to continue run');
    } finally {
      setPhaseBusy(false);
    }
  }

  /** Read-only cutover PREFLIGHT: maps each staged panel's customer domains, pins
   *  them to CF, and flags blockers. Always safe - it never changes item phase,
   *  so it can be re-run to re-derive the ledger after resolving a flag. */
  async function runPreflight(runId: string): Promise<void> {
    setPreflightBusy(true);
    try {
      const r = await api<{ enqueued: number }>(`/api/migration-runs/${runId}/preflight`, {
        method: 'POST',
      });
      showToast(
        r.enqueued > 0 ? 'success' : 'info',
        r.enqueued > 0
          ? `Preflight enqueued for ${r.enqueued} panel(s)`
          : 'No staged panels to preflight',
      );
      await refreshDetail(runId);
      // The worker fills the ledger after this POST without moving run status;
      // poll the detail until it lands so the operator does not have to reopen.
      if (r.enqueued > 0) startPreflightLoop(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Preflight failed');
    } finally {
      setPreflightBusy(false);
    }
  }

  /** Human-triggered CUTOVER: the first advance that touches user-visible mail, so
   *  it is gated behind a typed-CUTOVER confirm. The server re-checks staged +
   *  preflighted + no-flag and returns a 400/409 {error} we surface in the toast. */
  async function startCutover(runId: string): Promise<void> {
    setCutoverBusy(true);
    try {
      const r = await api<{ advanced: number }>(`/api/migration-runs/${runId}/cutover`, {
        method: 'POST',
        body: JSON.stringify({ confirm: 'CUTOVER' }),
      });
      showToast('success', `Cutover started for ${r.advanced} panel(s)`);
      setCutoverConfirmOpen(false);
      setCutoverTyped('');
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Cutover failed');
    } finally {
      setCutoverBusy(false);
    }
  }

  /** Human-triggered FINALIZE: the terminal advance (retire temp admin, re-apply
   *  aliases, re-suspend, exit the import). Gated behind a typed-FINALIZE confirm;
   *  the server refuses unless every panel is done at cutover. */
  async function finalizeRun(runId: string): Promise<void> {
    setFinalizeBusy(true);
    try {
      const r = await api<{ advanced: number }>(`/api/migration-runs/${runId}/finalize`, {
        method: 'POST',
        body: JSON.stringify({ confirm: 'FINALIZE' }),
      });
      showToast('success', `Finalize started for ${r.advanced} panel(s)`);
      setFinalizeConfirmOpen(false);
      setFinalizeTyped('');
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Finalize failed');
    } finally {
      setFinalizeBusy(false);
    }
  }

  /** Unlock path for a `password_missing` user: the operator types the real
   *  password, it is stamped manual, and once every user on the item has one the
   *  item clears the mirror gate on "Retry errors". */
  async function savePassword(runId: string, sourceEmail: string, userEmail: string): Promise<void> {
    const password = (pwDraft[`${sourceEmail}:${userEmail}`] ?? '').trim();
    if (password.length < 8) {
      showToast('error', 'Password must be at least 8 characters (Google minimum)');
      return;
    }
    try {
      await api(`/api/migration-runs/${runId}/users/password`, {
        method: 'PUT',
        body: JSON.stringify({ source_email: sourceEmail, user_email: userEmail, password }),
      });
      showToast('success', `Password saved for ${userEmail}`);
      setPwDraft((p) => {
        const next = { ...p };
        delete next[`${sourceEmail}:${userEmail}`];
        return next;
      });
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save password');
    }
  }

  /** Read the ops mailbox for ONE pair's destination admin@. On demand only:
   *  the operator opens this to watch the data-migration mail land, and the
   *  detail poll must not turn that into a Gmail request every 2.5s. */
  async function loadOpsMail(runId: string, sourceEmail: string): Promise<void> {
    setOpsMail((prev) => ({
      ...prev,
      [sourceEmail]: { loading: true, error: null, data: prev[sourceEmail]?.data ?? null },
    }));
    try {
      const d = await api<OpsMailResp>(
        `/api/migration-runs/${runId}/ops-mail?source_email=${encodeURIComponent(sourceEmail)}`,
      );
      setOpsMail((prev) => ({ ...prev, [sourceEmail]: { loading: false, error: null, data: d } }));
    } catch (err) {
      setOpsMail((prev) => ({
        ...prev,
        [sourceEmail]: {
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to read the ops mailbox',
          data: null,
        },
      }));
    }
  }

  function toggleOpsMail(runId: string, sourceEmail: string): void {
    if (opsOpen === sourceEmail) {
      setOpsOpen(null);
      return;
    }
    setOpsOpen(sourceEmail);
    if (!opsMail[sourceEmail]?.data) void loadOpsMail(runId, sourceEmail);
  }

  async function toggleAutoRetry(runId: string, enabled: boolean): Promise<void> {
    try {
      await api(`/api/migration-runs/${runId}/auto-retry`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      });
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Toggle failed');
    }
  }

  if (openRunId && detail) {
    const { run, items, users, domains } = detail;
    // "settled" gates the retry/continue actions: enabled once the run has
    // stopped moving on its own (paused/attention/staged/completed/failed).
    const terminal = isSettled(run.status);
    // Continue is offered only when some panel is parked done at a phase the
    // server can advance (snapshot/mirror/replicate); data_migrate-done is
    // staged and terminal, not continuable.
    const canContinue = items.some(
      (i) =>
        i.status === 'done' &&
        (i.phase === 'snapshot' || i.phase === 'mirror' || i.phase === 'replicate'),
    );
    const stagedCount = items.filter((i) => i.status === 'done' && i.phase === 'data_migrate').length;
    const erroredCount = items.filter((i) => i.status === 'error').length;
    // Overflow auto-split (migration 053), observability-only: how many customer
    // domains this (primary) run peeled onto companion overflow run(s). Overflow
    // runs carry included_domains instead, so this stays 0 on them.
    const splitOutCount = items.reduce((n, i) => n + (i.excluded_domains?.length ?? 0), 0);
    const movingCount = items.filter((i) => i.status === 'pending' || i.status === 'running').length;
    const itemStageChips = stageSummary(
      items.map((i) => ({ phase: i.phase, status: i.status, count: 1 })),
    );
    const usersBySource = new Map<string, RunUser[]>();
    for (const u of users) {
      const list = usersBySource.get(u.source_email) ?? [];
      list.push(u);
      usersBySource.set(u.source_email, list);
    }
    const domainsBySource = new Map<string, RunDomain[]>();
    for (const d of domains) {
      const list = domainsBySource.get(d.source_email) ?? [];
      list.push(d);
      domainsBySource.set(d.source_email, list);
    }
    // Cutover gating (staged runs only): a staged run parks at data_migrate
    // (offer "Start cutover") OR at cutover (offer "Finalize"); distinguish by
    // item phase, not run status. Preflight is always safe on a staged run.
    const cutoverDoneCount = items.filter(
      (i) => i.status === 'done' && i.phase === 'cutover',
    ).length;
    const someDoneAtDataMigrate = stagedCount > 0;
    const allDoneAtCutover = items.length > 0 && cutoverDoneCount === items.length;
    const pinnedDomains = domains.filter((d) => d.cf_zone_id !== null).length;
    const flaggedDomains = domains.filter((d) => d.flagged_reason !== null);
    // Flags no longer disable cutover (they defer); only a missing preflight
    // stamp does - the server 409s the same condition.
    const allPreflighted = items.length > 0 && items.every((i) => i.preflight_completed_at !== null);
    return (
      <div className="srm-root">
        <div className="srm-scroll">
          <div className="srm-runheader">
            <button
              type="button"
              className="srm-btn srm-btn--ghost srm-btn--small"
              onClick={() => setOpenRunId(null)}
            >
              ← Back
            </button>
            <h1 className="srm-title">Migration run</h1>
            <span className={`srm-status srm-status--${run.status}`}>{run.status}</span>
            {run.kind === 'overflow' && (
              <OverflowParentLink parentRunId={run.parent_run_id} onOpen={setOpenRunId} />
            )}
          </div>

          {toast && <div className={`srm-toast srm-toast--${toast.type}`}>{toast.message}</div>}

          <div className="srm-runmeta">
            <div>
              <strong>Created</strong>
              {fmtDate(run.created_at)}
            </div>
            <div>
              <strong>Triggered by</strong>
              {run.triggered_by ?? '-'}
            </div>
          </div>

          <div className="srm-runsummary">
            <span className="srm-summary-pill">{run.panels_total} total</span>
            <span className="srm-summary-pill srm-summary-pill--ok">{run.panels_done} done</span>
            <span className="srm-summary-pill srm-summary-pill--err">
              {run.panels_errored} errored
            </span>
            {splitOutCount > 0 && (
              <span
                className="srm-summary-pill"
                title="Customer domains peeled onto companion overflow run(s) - they cut over on the overflow run, not here"
              >
                {splitOutCount} split out
              </span>
            )}
          </div>

          {/* The pipeline, in execution order, hard stops included - click a
              phase for its per-panel sub-step breakdown. */}
          <PipelineRail
            items={items}
            activeStage={stageDrill}
            onSelect={(s) => {
              setStageDrill(s);
              setDrillExpanded(null);
            }}
          />

          <div className="srm-runactions">
            <label className="srm-filter">
              <input
                type="checkbox"
                checked={run.auto_retry_enabled}
                onChange={(e) => void toggleAutoRetry(run.id, e.target.checked)}
              />
              Auto-retry errored ({run.auto_retry_attempts_used}/{run.auto_retry_attempts_cap} used)
            </label>
            <div className="srm-actions-spacer" />
            <button
              type="button"
              className="srm-btn srm-btn--primary srm-btn--small"
              onClick={() => void retryErrors(run.id)}
              disabled={retrying || !terminal || run.panels_errored === 0}
              title={terminal ? 'Reset errored items and re-enqueue them' : 'Available once the run finishes'}
            >
              {retrying ? 'Retrying…' : `Retry errors (${run.panels_errored})`}
            </button>
            <button
              type="button"
              className="srm-btn srm-btn--primary srm-btn--small"
              onClick={() => void continueRun(run.id)}
              disabled={phaseBusy || !canContinue}
              title="Advance parked panels to their next phase (auto-advance handles healthy runs; this heals paused ones)"
            >
              Continue run
            </button>
            <a
              className="srm-btn srm-btn--ghost srm-btn--small"
              href={`/api/migration-runs/${run.id}/users.csv`}
              download
            >
              Download users CSV
            </a>
          </div>

          {/* 'attention' is included for the deferred-domains loop: a pass that
              skipped CF-flagged domains errors the item ('domains_deferred') and
              the run derives 'attention' - the operator must be able to re-run
              Preflight from here after fixing CF (then Retry errors). Start cutover
              is gated on run.status === 'staged' explicitly: a mixed 'attention' run
              can still have items done@data_migrate, and the server 409s a cutover
              start on any non-'staged' run - so the button must not offer it. */}
          {(run.status === 'staged' || run.status === 'attention') && (
            <div className="srm-cutover">
              <div className="srm-cutover-actions">
                <button
                  type="button"
                  className="srm-btn srm-btn--secondary srm-btn--small"
                  onClick={() => void runPreflight(run.id)}
                  disabled={preflightBusy || preflightRefreshing}
                  title="Map each staged panel's customer domains, pin them to Cloudflare, and flag blockers. Always safe - re-run after resolving a flag."
                >
                  {preflightBusy || preflightRefreshing ? 'Preflighting…' : 'Preflight'}
                </button>
                {run.status === 'staged' && someDoneAtDataMigrate && (
                  <button
                    type="button"
                    className="srm-btn srm-btn--danger srm-btn--small"
                    onClick={() => setCutoverConfirmOpen(true)}
                    disabled={cutoverBusy || !allPreflighted}
                    title={
                      allPreflighted
                        ? 'Advance every staged panel into cutover - the first step that touches user-visible mail. Deferred (CF-flagged) domains are skipped, not blockers.'
                        : 'Run Preflight first - every panel needs a completed preflight before cutover.'
                    }
                  >
                    Start cutover ({stagedCount})
                  </button>
                )}
                {allDoneAtCutover && (
                  <button
                    type="button"
                    className="srm-btn srm-btn--danger srm-btn--small"
                    onClick={() => setFinalizeConfirmOpen(true)}
                    disabled={finalizeBusy}
                    title="Retire the temp admin, re-apply aliases, re-suspend, and exit the import - the terminal advance."
                  >
                    Finalize ({cutoverDoneCount})
                  </button>
                )}
              </div>

              {domains.length > 0 && (
                <div className="srm-preflight-summary">
                  {pinnedDomains} domain{pinnedDomains === 1 ? '' : 's'} pinned,{' '}
                  {flaggedDomains.length} flagged
                </div>
              )}

              {flaggedDomains.length > 0 && (
                <div className="srm-flagbanner">
                  <strong>Deferred domains (skipped at cutover until moved into our CF):</strong>
                  <ul className="srm-flagbanner-list">
                    {flaggedDomains.map((d) => (
                      <li key={`${d.source_email}:${d.domain}`}>
                        <code className="srm-cell-mono">{d.domain}</code> - {d.flagged_reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {cutoverConfirmOpen && (
                <div className="srm-confirm-row">
                  <span>
                    Advance {stagedCount} staged panel(s) into cutover. This is the first step
                    that mutates user-visible mail.
                    {flaggedDomains.length > 0 &&
                      ` ${flaggedDomains.length} deferred domain(s) will be SKIPPED - their mail keeps flowing to the source until CF is fixed, Preflight re-run, and errors retried.`}{' '}
                    Type <strong>CUTOVER</strong> to confirm:
                  </span>
                  <input
                    type="text"
                    className="srm-confirm-input"
                    placeholder="Type CUTOVER"
                    value={cutoverTyped}
                    onChange={(e) => setCutoverTyped(e.target.value)}
                    disabled={cutoverBusy}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="srm-btn srm-btn--danger srm-btn--small"
                    onClick={() => void startCutover(run.id)}
                    disabled={cutoverTyped !== 'CUTOVER' || cutoverBusy}
                  >
                    {cutoverBusy ? 'Starting…' : 'Start cutover'}
                  </button>
                  <button
                    type="button"
                    className="srm-btn srm-btn--ghost srm-btn--small"
                    onClick={() => {
                      setCutoverConfirmOpen(false);
                      setCutoverTyped('');
                    }}
                    disabled={cutoverBusy}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {finalizeConfirmOpen && (
                <div className="srm-confirm-row">
                  <span>
                    Finalize {cutoverDoneCount} panel(s): retire the temp admin, re-apply
                    aliases, re-suspend, exit the import. Type <strong>FINALIZE</strong> to
                    confirm:
                  </span>
                  <input
                    type="text"
                    className="srm-confirm-input"
                    placeholder="Type FINALIZE"
                    value={finalizeTyped}
                    onChange={(e) => setFinalizeTyped(e.target.value)}
                    disabled={finalizeBusy}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="srm-btn srm-btn--danger srm-btn--small"
                    onClick={() => void finalizeRun(run.id)}
                    disabled={finalizeTyped !== 'FINALIZE' || finalizeBusy}
                  >
                    {finalizeBusy ? 'Finalizing…' : 'Finalize'}
                  </button>
                  <button
                    type="button"
                    className="srm-btn srm-btn--ghost srm-btn--small"
                    onClick={() => {
                      setFinalizeConfirmOpen(false);
                      setFinalizeTyped('');
                    }}
                    disabled={finalizeBusy}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="srm-stagestrip">
            <span className="srm-stagestrip-counts">
              {movingCount} moving
              <span className="srm-stagestrip-sep">·</span>
              {stagedCount}/{items.length} staged
              <span className="srm-stagestrip-sep">·</span>
              {erroredCount} errored
            </span>
            {itemStageChips.length > 0 && (
              <span className="srm-stagecell">
                {itemStageChips.map((c) => (
                  <span key={c.label} className={`srm-stagechip srm-stagechip--${c.kind}`}>
                    {c.label}
                  </span>
                ))}
              </span>
            )}
          </div>

          {stageDrill !== null && (
            <section className="srm-card">
              <div className="srm-card-header">
                <h2 className="srm-card-title">
                  {RAIL.find((s) => s.key === stageDrill)?.label ?? stageDrill} · breakdown
                </h2>
                <input
                  className="srm-picker-search srm-drill-search"
                  type="text"
                  placeholder="Search panels, users, domains…"
                  value={drillSearch}
                  onChange={(e) => setDrillSearch(e.target.value)}
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="srm-btn srm-btn--ghost srm-btn--small"
                  onClick={() => setStageDrill(null)}
                >
                  ✕ Close
                </button>
              </div>
              <StageDrill
                stage={stageDrill}
                items={items}
                usersBySource={usersBySource}
                domainsBySource={domainsBySource}
                search={drillSearch}
                expanded={drillExpanded}
                onToggle={(s) => setDrillExpanded(drillExpanded === s ? null : s)}
              />
            </section>
          )}

          <section className="srm-card">
            <div className="srm-card-header">
              <h2 className="srm-card-title">Panel pairs</h2>
            </div>
            <div className="srm-table-wrap">
              <table className="srm-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Destination</th>
                    <th>Phase</th>
                    <th className="srm-col-center">Status</th>
                    <th className="srm-col-num">Users</th>
                    <th
                      className="srm-col-num"
                      title="Seats STILL needed on the destination (users not yet created there) / free seats on the destination, as of the last capacity gate."
                    >
                      Need/Free
                    </th>
                    <th>Error</th>
                    <th
                      className="srm-col-center"
                      title="Mail Google's Data Migration Service sent to this destination panel's admin@, read out of the ops mailbox."
                    >
                      Ops mail
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const expanded = expandedItem === it.source_email;
                    const itemUsers = usersBySource.get(it.source_email) ?? [];
                    // The password input is offered ONLY when the item is truly
                    // blocked on passwords. Right after snapshot every user has a
                    // null password (fkgsheets has not run yet) - inviting an entry
                    // there would stamp password_source='manual' and permanently
                    // switch OFF the fkgsheets resolve for that user, which is the
                    // primary path.
                    const pwBlocked = it.status === 'error' && it.error_code === 'password_missing';
                    const fullError = it.error_message
                      ? `${it.error_code ?? ''} ${it.error_message}`.trim()
                      : '';
                    // Overflow auto-split scope (migration 053), read-only. A parent
                    // item lists the domains peeled OUT (excluded); an overflow item
                    // lists the domain(s) it is scoped TO (included). Only one is ever
                    // populated on a given item.
                    const splitDomains = it.excluded_domains ?? [];
                    const scopeDomains = it.included_domains ?? [];
                    return [
                      <tr
                        key={it.source_email}
                        className="srm-row--clickable"
                        onClick={() =>
                          setExpandedItem(expanded ? null : it.source_email)
                        }
                      >
                        <td className="srm-cell-mono">
                          {it.source_email}
                          {splitDomains.length > 0 && (
                            <div className="srm-stagecell">
                              {splitDomains.map((d) => (
                                <span
                                  key={d}
                                  className="srm-stagechip srm-stagechip--staged"
                                  title="Peeled onto a companion overflow run - excluded from this panel's cutover"
                                >
                                  {d}
                                </span>
                              ))}
                            </div>
                          )}
                          {scopeDomains.length > 0 && (
                            <div className="srm-stagecell">
                              {scopeDomains.map((d) => (
                                <span
                                  key={d}
                                  className="srm-stagechip srm-stagechip--active"
                                  title="This overflow item is scoped to just this domain"
                                >
                                  {d}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="srm-cell-mono">{it.dest_email}</td>
                        <StepperCell item={it} users={itemUsers} />
                        <td className="srm-col-center">
                          <span className={`srm-status srm-status--${it.status}`}>{it.status}</span>
                        </td>
                        <td className="srm-col-num">
                          {it.users_total ?? '-'}
                          {(it.core_domain_users ?? 0) > 0 && (
                            <>
                              {' '}
                              <span
                                className="srm-flag"
                                title="Users on the source CORE domain - these addresses cannot move; resolve before any later phase."
                              >
                                {it.core_domain_users} core
                              </span>
                            </>
                          )}
                        </td>
                        <td className="srm-col-num">
                          {it.users_needed != null && it.dest_free_seats != null
                            ? `${it.users_needed}/${it.dest_free_seats}`
                            : '-'}
                          {it.error_code === 'insufficient_capacity' && (
                            <>
                              {' '}
                              <span
                                className="srm-flag"
                                title="Destination does not have enough free seats"
                              >
                                no seats
                              </span>
                            </>
                          )}
                        </td>
                        <td className="srm-cell-error" title={fullError || undefined}>
                          {fullError ? truncateError(fullError) : '-'}
                        </td>
                        <td className="srm-col-center">
                          <button
                            type="button"
                            className="srm-btn srm-btn--ghost srm-btn--small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOpsMail(run.id, it.source_email);
                            }}
                            title={`Read mail sent to ${it.dest_email}`}
                          >
                            {opsOpen === it.source_email ? 'Hide' : 'Mail'}
                          </button>
                        </td>
                      </tr>,
                      ...(opsOpen === it.source_email
                        ? [
                            <tr key={`${it.source_email}:opsmail`} className="srm-userrow">
                              <td className="srm-opsmail" colSpan={8}>
                                <OpsMailPanel
                                  state={opsMail[it.source_email]}
                                  destEmail={it.dest_email}
                                  onRefresh={() => void loadOpsMail(run.id, it.source_email)}
                                />
                              </td>
                            </tr>,
                          ]
                        : []),
                      ...(expanded
                        ? [
                            <tr
                              key={`${it.source_email}:domains`}
                              className="srm-userrow"
                            >
                              <td className="srm-domaincell" colSpan={8}>
                                <div className="srm-domain-head">Customer domains</div>
                                <DomainTable
                                  domains={domainsBySource.get(it.source_email) ?? []}
                                />
                                {/* The LAST per-panel step: realized users pushed to the
                                    external IMAPs orchestrator. Deferred-domain users show
                                    as the gap until their swap pass catches them up. */}
                                <div className="srm-domain-empty">
                                  IMAPs push:{' '}
                                  {itemUsers.filter((u) => u.imaps_pushed_at !== null).length}/
                                  {itemUsers.length} users pushed
                                </div>
                              </td>
                            </tr>,
                          ]
                        : []),
                      ...(expanded
                        ? itemUsers.map((u) => {
                            const needsPw = pwBlocked && u.password === null;
                            return (
                              <tr
                                key={`${it.source_email}:${u.user_email}`}
                                className={`srm-userrow ${needsPw ? 'srm-userrow--locked' : ''}`}
                              >
                                <td className="srm-cell-mono">
                                  {u.user_email}
                                  {u.full_name && (
                                    <div className="srm-cell-faint">{u.full_name}</div>
                                  )}
                                </td>
                                <td className="srm-cell-mono">{u.dest_holding_email ?? '-'}</td>
                                <td className="srm-cell-mono">
                                  {u.password ? (
                                    <>
                                      {u.password}
                                      {u.password_source === 'manual' && (
                                        <>
                                          {' '}
                                          <span className="srm-flag" title="Entered by an operator">
                                            manual
                                          </span>
                                        </>
                                      )}
                                    </>
                                  ) : needsPw ? (
                                    <span className="srm-pw-entry">
                                      <input
                                        className="srm-picker-dest"
                                        type="text"
                                        placeholder="enter password to unlock"
                                        value={pwDraft[`${it.source_email}:${u.user_email}`] ?? ''}
                                        onChange={(e) =>
                                          setPwDraft((p) => ({
                                            ...p,
                                            [`${it.source_email}:${u.user_email}`]: e.target.value,
                                          }))
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        spellCheck={false}
                                      />
                                      <button
                                        type="button"
                                        className="srm-btn srm-btn--primary srm-btn--small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void savePassword(run.id, it.source_email, u.user_email);
                                        }}
                                      >
                                        Save
                                      </button>
                                    </span>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                {/* Snapshot state. `suspended` matters at cutover: the
                                    mirror deliberately creates suspended source users
                                    ACTIVE on the destination, so this is the operator's
                                    only cue to re-suspend them afterwards. */}
                                <td className="srm-userstate">
                                  {u.suspended && (
                                    <span
                                      className="srm-flag srm-flag--warn"
                                      title="Suspended on the source - it is re-created ACTIVE on the destination; re-suspend after cutover"
                                    >
                                      suspended
                                    </span>
                                  )}
                                  {u.flags.includes('core_domain') && (
                                    <span
                                      className="srm-flag"
                                      title="On the source CORE domain - this address cannot move; resolve before cutover"
                                    >
                                      core
                                    </span>
                                  )}
                                  {u.aliases.length > 0 && (
                                    <span
                                      className="srm-cell-faint"
                                      title={u.aliases.join(', ')}
                                    >
                                      {u.aliases.length} alias
                                      {u.aliases.length === 1 ? '' : 'es'}
                                    </span>
                                  )}
                                  {!u.suspended &&
                                    !u.flags.includes('core_domain') &&
                                    u.aliases.length === 0 &&
                                    '-'}
                                </td>
                                <td>
                                  {u.created_on_dest_at ? 'created' : '-'}
                                  {u.license_assigned_at ? ' - licensed' : ''}
                                  {u.photo_copied_at ? ' - photo' : ''}
                                  {u.imaps_pushed_at ? ' - imaps' : ''}
                                </td>
                                {/* Spans the remaining columns of the pair row
                                    (Error + Ops mail) - a user row has no ops
                                    mail of its own. */}
                                <td
                                  className="srm-cell-error"
                                  colSpan={3}
                                  title={u.error_message ?? undefined}
                                >
                                  {u.error_message ? truncateError(u.error_message) : ''}
                                </td>
                              </tr>
                            );
                          })
                        : []),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="srm-root">
      <div className="srm-scroll">
        <header className="srm-header">
          <h1 className="srm-title">Serken Renewal Migration</h1>
          <p className="srm-subtitle">
            Migrate everything off an expiring Serken panel to a fresh panel with zero
            user-visible change. Automatic through{' '}
            <strong>snapshot → allocate → mirror → replicate → data&nbsp;migrate</strong>, then a{' '}
            <strong>hard stop</strong> (run = staged) where you Preflight and type CUTOVER, and a
            second hard stop before FINALIZE. Mirror claims seats FIRST (random passwords),
            blocks the source's license pool, and resolves real passwords last - an
            unresolvable password never blocks a run. Open a run to see the full pipeline and
            click any stage for its per-panel breakdown.
          </p>
        </header>

        {toast && <div className={`srm-toast srm-toast--${toast.type}`}>{toast.message}</div>}

        <section className="srm-card">
          <div className="srm-card-header">
            <h2 className="srm-card-title">Select source panels</h2>
          </div>
          <div className="srm-card-body">
            <input
              className="srm-picker-search"
              type="text"
              placeholder="Search panels by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <datalist id="srm-panel-emails">
              {panels.map((p) => (
                <option key={p.id} value={p.email} />
              ))}
            </datalist>
            <div className="srm-picker-list">
              {filteredPanels.map((p) => {
                const checked = selectedSources.has(p.email);
                return (
                  <div
                    key={p.id}
                    className={`srm-picker-row ${checked ? 'srm-picker-row--selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSource(p.email)}
                    />
                    <span className="srm-cell-mono">{p.email}</span>
                    {checked && (
                      <input
                        className="srm-picker-dest"
                        type="text"
                        list="srm-panel-emails"
                        placeholder="destination admin@…"
                        value={destBySource[p.email] ?? ''}
                        onChange={(e) =>
                          setDestBySource((prev) => ({ ...prev, [p.email]: e.target.value }))
                        }
                        spellCheck={false}
                      />
                    )}
                  </div>
                );
              })}
              {filteredPanels.length === 0 && (
                <div className="srm-picker-row">No panels match.</div>
              )}
            </div>
          </div>
        </section>

        <section className="srm-card">
          <div className="srm-card-header">
            <h2 className="srm-card-title">Or bulk paste pairs</h2>
          </div>
          <div className="srm-card-body">
            <label className="srm-field">
              <span className="srm-field-label">
                One <code>source_email,dest_email</code> per line
                {bulkCount.valid > 0 && ` - ${bulkCount.valid} parsed`}
                {bulkCount.invalid > 0 &&
                  ` (${bulkCount.invalid} invalid - run will be rejected)`}
              </span>
              <textarea
                className="srm-textarea srm-mono"
                rows={8}
                placeholder={'admin@theserkenlabs1.xyz,admin@freshpanel1.shop\nadmin@theserkenlabs2.xyz,admin@freshpanel2.shop'}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                spellCheck={false}
              />
            </label>
            <div className="srm-actions-row">
              <div className="srm-actions-spacer" />
              <button
                type="button"
                className="srm-btn srm-btn--primary"
                onClick={() => void startRun()}
                disabled={starting || totalPairs === 0 || bulkCount.invalid > 0}
              >
                {starting
                  ? 'Starting…'
                  : `Start migration run (${totalPairs} pair${totalPairs === 1 ? '' : 's'})`}
              </button>
            </div>
          </div>
        </section>

        {cfMissing.length > 0 && (
          <section className="srm-card">
            <div className="srm-card-header">
              <h2 className="srm-card-title">CF-missing domains ({cfMissing.length})</h2>
            </div>
            <div className="srm-cfmissing-hint">
              Deferred at cutover until fixed - remedy depends on the reason:{' '}
              <code>zone_not_found</code> / <code>zone_not_active</code> - move the DNS into
              one of our Cloudflare accounts; <code>groups_on_domain</code> - delete the Google
              Groups on the domain. Then re-run Preflight on the run and Retry errors - the retry
              re-runs the delta first; mail keeps flowing to the source until the swap.
            </div>
            <div className="srm-table-wrap">
              <table className="srm-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Panel</th>
                    <th>Reason</th>
                    <th className="srm-col-num">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {cfMissing.map((d) => (
                    <tr key={`${d.run_id}:${d.source_email}:${d.domain}`}>
                      <td className="srm-cell-mono">{d.domain}</td>
                      <td className="srm-cell-mono">{d.source_email}</td>
                      <td>{d.flagged_reason}</td>
                      <td className="srm-col-num">{d.users_on_domain}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="srm-card">
          <div className="srm-card-header">
            <h2 className="srm-card-title">Recent runs</h2>
          </div>
          <div className="srm-table-wrap">
            <table className="srm-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th className="srm-col-center">Status</th>
                  <th>Stage</th>
                  <th className="srm-col-num">Done</th>
                  <th className="srm-col-num">Errored</th>
                  <th className="srm-col-num">Total</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="srm-empty">
                      No runs yet.
                    </td>
                  </tr>
                ) : (
                  runs.map((r) => (
                    <tr key={r.id} className="srm-row--clickable" onClick={() => setOpenRunId(r.id)}>
                      <td className="srm-cell-mono">{fmtDate(r.created_at)}</td>
                      <td className="srm-col-center">
                        <span className={`srm-status srm-status--${r.status}`}>{r.status}</span>
                      </td>
                      <td className="srm-stagecell">
                        {r.kind === 'overflow' && (
                          <OverflowParentLink parentRunId={r.parent_run_id} onOpen={setOpenRunId} />
                        )}
                        {stageSummary(r.stage_counts).map((c) => (
                          <span key={c.label} className={`srm-stagechip srm-stagechip--${c.kind}`}>
                            {c.label}
                          </span>
                        ))}
                      </td>
                      <td className="srm-col-num">{r.panels_done}</td>
                      <td className="srm-col-num">{r.panels_errored}</td>
                      <td className="srm-col-num">{r.panels_total}</td>
                      <td className="srm-cell-faint">{r.triggered_by ?? '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
